import type { Concept } from '@yuan-math/curriculum';

/**
 * The Grades 3–5 slice: every concept here has a lesson and exercises behind
 * it. This is the part of the graph a student can actually walk today.
 *
 * `levels` lists where a concept is *typically* taught, never where it is
 * locked to. Fractions appear in Grades 3, 4 and 5 because different curricula
 * place them differently, and a Grade 6 student with a gap must be able to
 * reach back for them without being told they are doing Grade 3 work.
 */
export const ELEMENTARY_CONCEPTS: Concept[] = [
  /* ---------------------------------------------------------------- numbers */
  {
    id: 'counting',
    title: 'Counting and Quantity',
    summary: 'Numbers name how many, and each number is one more than the one before it.',
    strand: 'early-math',
    levels: ['kindergarten', 'grade-1'],
    prerequisites: [],
    status: 'available',
    estimatedMinutes: 20,
  },
  {
    id: 'place-value',
    title: 'Place Value',
    summary: 'A digit means ten times more when it moves one place to the left.',
    strand: 'numbers',
    levels: ['grade-2', 'grade-3', 'grade-4'],
    prerequisites: ['counting'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'rounding',
    title: 'Rounding',
    summary: 'Replacing a number with a nearby simpler one, on purpose and by a rule.',
    strand: 'numbers',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['place-value'],
    status: 'available',
    estimatedMinutes: 20,
  },

  /* ------------------------------------------------------------- arithmetic */
  {
    id: 'multi-digit-addition',
    title: 'Multi-Digit Addition',
    summary: 'Adding column by column, carrying a ten when a column overflows.',
    strand: 'arithmetic',
    levels: ['grade-2', 'grade-3'],
    prerequisites: ['place-value'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'multi-digit-subtraction',
    title: 'Multi-Digit Subtraction',
    summary: 'Subtracting column by column, regrouping when a column runs short.',
    strand: 'arithmetic',
    levels: ['grade-2', 'grade-3'],
    prerequisites: ['multi-digit-addition'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'multiplication-facts',
    title: 'Multiplication',
    summary: 'Multiplication counts equal groups — and an array shows why order does not matter.',
    strand: 'arithmetic',
    levels: ['grade-3'],
    prerequisites: ['multi-digit-addition'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'division-meaning',
    title: 'Division',
    summary: 'Sharing into equal groups, and asking how many groups fit.',
    strand: 'arithmetic',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multiplication-facts'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'division-remainders',
    title: 'Remainders',
    summary: 'What is left over when the sharing does not come out even — and what it means.',
    strand: 'arithmetic',
    levels: ['grade-4'],
    prerequisites: ['division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'factors-multiples',
    title: 'Factors and Multiples',
    summary: 'Which numbers divide a number exactly, and which numbers it divides.',
    strand: 'number-theory',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'order-of-operations',
    title: 'Order of Operations',
    summary: 'A shared convention so every reader of an expression gets the same answer.',
    strand: 'arithmetic',
    levels: ['grade-5', 'grade-6'],
    prerequisites: ['multiplication-facts', 'division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* -------------------------------------------------------------- fractions */
  {
    id: 'fraction-basics',
    title: 'What a Fraction Is',
    summary: 'The denominator names the size of the pieces; the numerator counts them.',
    strand: 'fractions',
    levels: ['grade-3'],
    prerequisites: ['division-meaning'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'equivalent-fractions',
    title: 'Equivalent Fractions',
    summary: 'Cutting every piece into the same number of smaller pieces changes the name, not the amount.',
    strand: 'fractions',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['fraction-basics', 'multiplication-facts'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'simplify-fractions',
    title: 'Simplifying Fractions',
    summary: 'Writing a fraction with the largest pieces it can have.',
    strand: 'fractions',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['equivalent-fractions', 'factors-multiples'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'compare-fractions',
    title: 'Comparing Fractions',
    summary: 'Which is more — and why a bigger denominator means smaller pieces.',
    strand: 'fractions',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['equivalent-fractions'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'common-denominators',
    title: 'Common Denominators',
    summary: 'Rewriting two fractions with pieces of the same size, so they can be compared or combined.',
    strand: 'fractions',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['equivalent-fractions', 'factors-multiples'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'add-fractions',
    title: 'Adding and Subtracting Fractions',
    summary: 'Same-size pieces can be counted together; different-size pieces cannot.',
    strand: 'fractions',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['common-denominators', 'add-like-fractions'],
    status: 'available',
    estimatedMinutes: 35,
  },
  {
    id: 'multiply-fractions',
    title: 'Multiplying Fractions',
    summary: 'Multiplying by a fraction takes a part of something — so the answer can get smaller.',
    strand: 'fractions',
    levels: ['grade-5'],
    prerequisites: ['fraction-times-whole'],
    status: 'available',
    estimatedMinutes: 30,
  },
  {
    id: 'mixed-numbers',
    title: 'Mixed Numbers',
    summary: 'Two ways to write the same amount: wholes and parts, or parts alone.',
    strand: 'fractions',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['fraction-basics', 'division-remainders'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* --------------------------------------------------------------- decimals */
  {
    id: 'decimal-place-value',
    title: 'Decimal Place Value',
    summary: 'Place value keeps going past the ones: tenths, hundredths, thousandths.',
    strand: 'decimals',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['place-value', 'fraction-basics'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'compare-decimals',
    title: 'Comparing Decimals',
    summary: 'Compare place by place from the left — more digits does not mean bigger.',
    strand: 'decimals',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['decimal-place-value'],
    status: 'available',
    estimatedMinutes: 20,
  },
  {
    id: 'add-decimals',
    title: 'Adding and Subtracting Decimals',
    summary: 'Line up the decimal points, and it is ordinary column arithmetic.',
    strand: 'decimals',
    levels: ['grade-4', 'grade-5'],
    prerequisites: ['decimal-place-value', 'multi-digit-addition'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* --------------------------------------------------------------- geometry */
  {
    id: 'perimeter',
    title: 'Perimeter',
    summary: 'The distance all the way around a shape.',
    strand: 'geometry',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multi-digit-addition'],
    status: 'available',
    estimatedMinutes: 20,
  },
  {
    id: 'area-rectangles',
    title: 'Area of Rectangles',
    summary: 'How many unit squares fit inside — and why that is length times width.',
    strand: 'geometry',
    levels: ['grade-3', 'grade-4'],
    prerequisites: ['multiplication-facts', 'perimeter'],
    status: 'available',
    estimatedMinutes: 25,
  },

  /* ---------------------------------------------------------------- algebra */
  {
    id: 'variables',
    title: 'Variables',
    summary: 'A letter stands for a number we do not know yet — not for a thing.',
    strand: 'algebra',
    levels: ['grade-5', 'grade-6'],
    prerequisites: ['numerical-expressions'],
    status: 'available',
    estimatedMinutes: 25,
  },
  {
    id: 'one-step-equations',
    title: 'One-Step Equations',
    summary: 'An equation is a balance: whatever you do to one side, do to the other.',
    strand: 'algebra',
    levels: ['grade-5', 'grade-6'],
    prerequisites: ['variables'],
    status: 'available',
    estimatedMinutes: 30,
  },
];
